import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { PageResponse, Procedure, Structure } from '../types';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  FunnelIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  RocketLaunchIcon,
  Squares2X2Icon,
  TableCellsIcon,
  CalendarDaysIcon,
  BarsArrowDownIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import ProcedureGanttChart from '../components/ProcedureGanttChart';

type ViewMode = 'cards' | 'table' | 'gantt';

const PAGE_SIZE = 12;

// ─── Helpers ────────────────────────────────────────────────────────────────

const STATUT_CONFIG = {
  en_attente: {
    label: 'En attente',
    badgeCls: 'bg-gray-100 text-gray-600 ring-gray-200',
    headerCls: 'from-gray-50 to-gray-100 border-gray-200',
    barCls: 'bg-gray-400',
    icon: ClockIcon,
    dotCls: 'bg-gray-400',
  },
  en_cours: {
    label: 'En cours',
    badgeCls: 'bg-blue-50 text-blue-700 ring-blue-200',
    headerCls: 'from-blue-50 to-indigo-50 border-blue-100',
    barCls: 'bg-blue-500',
    icon: ArrowPathIcon,
    dotCls: 'bg-blue-500',
  },
  terminee: {
    label: 'Terminée',
    badgeCls: 'bg-green-50 text-green-700 ring-green-200',
    headerCls: 'from-green-50 to-emerald-50 border-green-100',
    barCls: 'bg-green-500',
    icon: CheckCircleIcon,
    dotCls: 'bg-green-500',
  },
  en_exploitation: {
    label: 'En exploitation',
    badgeCls: 'bg-amber-50 text-amber-700 ring-amber-300',
    headerCls: 'from-amber-50 to-yellow-50 border-amber-200',
    barCls: 'bg-amber-500',
    icon: RocketLaunchIcon,
    dotCls: 'bg-amber-500',
  },
};

function getDisplayStatut(procedure: import('../types').Procedure): keyof typeof STATUT_CONFIG {
  if (procedure.statut === 'EN_EXPLOITATION') return 'en_exploitation';
  if (procedure.ponderationScore === 100) return 'terminee';
  if (procedure.ponderationScore > 0) return 'en_cours';
  return 'en_attente';
}

// ─── Skeleton Card ───────────────────────────────────────────────────────────

const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden animate-pulse">
    <div className="h-2 bg-gray-200 w-full" />
    <div className="p-5 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gray-200 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-100 rounded w-1/2" />
        </div>
        <div className="h-6 w-20 bg-gray-200 rounded-full" />
      </div>
      <div className="space-y-1">
        <div className="flex justify-between">
          <div className="h-3 bg-gray-100 rounded w-16" />
          <div className="h-3 bg-gray-100 rounded w-8" />
        </div>
        <div className="h-2 bg-gray-200 rounded-full w-full" />
      </div>
      <div className="flex justify-between pt-1 border-t border-gray-100">
        <div className="h-3 bg-gray-100 rounded w-24" />
        <div className="h-3 bg-gray-100 rounded w-16" />
      </div>
    </div>
    <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
      <div className="h-8 w-8 bg-gray-200 rounded-lg" />
      <div className="h-8 w-8 bg-gray-200 rounded-lg" />
    </div>
  </div>
);

// ─── Procedure Card ──────────────────────────────────────────────────────────

interface ProcedureCardProps {
  procedure: Procedure;
  canEdit: boolean;
  isOwnStructure: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

const ProcedureCard: React.FC<ProcedureCardProps> = ({
  procedure,
  canEdit,
  isOwnStructure,
  onEdit,
  onDelete,
}) => {
  const statut = getDisplayStatut(procedure);
  const cfg = STATUT_CONFIG[statut];
  const StatutIcon = cfg.icon;

  const initials = procedure.structureNom
    ? procedure.structureNom
        .split(' ')
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase()
    : '—';

  return (
    <div className="group bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all duration-200 flex flex-col">
      {/* Bande colorée en haut selon l'état */}
      <div className={`h-1.5 w-full ${cfg.dotCls}`} />

      {/* En-tête coloré */}
      <div className={`bg-gradient-to-r ${cfg.headerCls} border-b px-5 pt-4 pb-3`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            {/* Avatar structure */}
            {procedure.logoUrl ? (
              <img
                src={procedure.logoUrl}
                alt={procedure.structureNom ?? ''}
                className="w-11 h-11 rounded-xl object-cover flex-shrink-0 ring-2 ring-white shadow-sm"
              />
            ) : (
              <div className="w-11 h-11 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 ring-2 ring-white shadow-sm">
                <span className="text-white text-xs font-bold">{initials}</span>
              </div>
            )}

            <div className="min-w-0">
              <Link
                to={`/procedures/${procedure.id}`}
                className="block text-sm font-semibold text-gray-900 line-clamp-2 hover:text-primary-700 transition-colors leading-snug"
              >
                {procedure.nom}
              </Link>
              <p className="text-xs text-gray-500 mt-0.5 truncate flex items-center gap-1">
                <BuildingOffice2Icon className="h-3 w-3 flex-shrink-0" />
                <span title="Structure de développement">{procedure.structureNom ?? '—'}</span>
              </p>
              {procedure.structureBeneficiaireNom && (
                <p className="text-xs text-indigo-600 mt-0.5 truncate flex items-center gap-1">
                  <svg className="h-3 w-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span title="Structure bénéficiaire">{procedure.structureBeneficiaireNom}</span>
                </p>
              )}
            </div>
          </div>

          {/* Badge état pill */}
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ring-1 whitespace-nowrap flex-shrink-0 ${cfg.badgeCls}`}
          >
            <StatutIcon className="h-3 w-3" />
            {cfg.label}
          </span>
        </div>

        {/* Bannière En exploitation */}
        {statut === 'en_exploitation' && (
          <div className="mt-2 flex items-center gap-1.5 bg-amber-100 border border-amber-300 rounded-lg px-2.5 py-1 w-fit">
            <RocketLaunchIcon className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
            <span className="text-xs font-semibold text-amber-700">Score 100% — Passée en exploitation</span>
          </div>
        )}

        {/* Badge "Votre structure" pour l'utilisateur */}
        {isOwnStructure && (
          <div className="mt-2">
            <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 bg-primary-50 px-2 py-0.5 rounded-full ring-1 ring-primary-200">
              <CheckCircleIconSolid className="h-3 w-3" />
              Votre structure
            </span>
          </div>
        )}
      </div>

      {/* Corps */}
      <Link to={`/procedures/${procedure.id}`} className="block flex-1 px-5 py-4 space-y-3">
        {/* Barre de progression */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-500 font-medium">Progression</span>
            <span className="text-xs font-bold text-gray-800">{procedure.ponderationScore}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className={`${cfg.barCls} h-2 rounded-full transition-all duration-700 ease-out`}
              style={{ width: `${procedure.ponderationScore}%` }}
            />
          </div>
        </div>

        {/* Stats footer */}
        <div className="flex items-center justify-between text-xs text-gray-500 pt-1 border-t border-gray-100">
          <span className="flex items-center gap-1">
            <DocumentTextIcon className="h-3.5 w-3.5" />
            {procedure.tachesCompleted}/{procedure.tachesTotal} tâche
            {procedure.tachesTotal !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Année */}
        <div className="flex items-center justify-end">
          <span className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full ring-1 ring-gray-200">
            {procedure.annee}
          </span>
        </div>
      </Link>

      {/* Actions */}
      {canEdit && (
        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-1.5">
          <button
            onClick={onEdit}
            title="Modifier"
            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            title="Supprimer"
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Pagination ───────────────────────────────────────────────────────────────

interface PaginationProps {
  page: number;
  totalPages: number;
  totalElements: number;
  size: number;
  onPageChange: (p: number) => void;
}

const PaginationBar: React.FC<PaginationProps> = ({
  page,
  totalPages,
  totalElements,
  size,
  onPageChange,
}) => {
  if (totalPages <= 1) return null;

  const from = page * size + 1;
  const to = Math.min((page + 1) * size, totalElements);

  const pages: (number | '…')[] = [];
  if (totalPages <= 7) {
    for (let i = 0; i < totalPages; i++) pages.push(i);
  } else {
    pages.push(0);
    if (page > 2) pages.push('…');
    for (let i = Math.max(1, page - 1); i <= Math.min(totalPages - 2, page + 1); i++)
      pages.push(i);
    if (page < totalPages - 3) pages.push('…');
    pages.push(totalPages - 1);
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
      <p className="text-sm text-gray-500">
        Affichage{' '}
        <span className="font-medium text-gray-700">
          {from}–{to}
        </span>{' '}
        sur{' '}
        <span className="font-medium text-gray-700">{totalElements}</span> procédures
      </p>

      <nav className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 0}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>

        {pages.map((p, idx) =>
          p === '…' ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 text-sm select-none">
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p as number)}
              className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                p === page
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {(p as number) + 1}
            </button>
          )
        )}

        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages - 1}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </nav>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const Procedures: React.FC = () => {
  const { isSuperAdmin, isResponsable, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [result, setResult] = useState<PageResponse<Procedure>>({
    content: [],
    page: 0,
    size: PAGE_SIZE,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: true,
  });
  const [structures, setStructures] = useState<Structure[]>([]);
  const [years, setYears] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedStructure, setSelectedStructure] = useState(() => searchParams.get('structureId') ?? '');
  const [selectedBeneficiaire, setSelectedBeneficiaire] = useState('');
  const [selectedYear, setSelectedYear] = useState<number | ''>('');
  const [selectedSort, setSelectedSort] = useState('createdAt_desc');
  const [currentPage, setCurrentPage] = useState(0);

  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [ganttProcedures, setGanttProcedures] = useState<Procedure[]>([]);
  const [ganttLoading, setGanttLoading] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [procedureToDelete, setProcedureToDelete] = useState<Procedure | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Pour UTILISATEUR : forcer le filtre sur sa structure
  const isUtilisateur = !isSuperAdmin() && !isResponsable();
  const structureFixe = isUtilisateur ? (user?.structureId ?? '') : '';

  const fetchProcedures = useCallback(
    async (page: number, searchTerm: string, structureId: string, beneficiaireId: string, annee: number | '', sort: string) => {
      setIsLoading(true);
      try {
        const [sortBy, sortDir] = sort.split('_');
        const data = await apiService.getProcedures({
          page,
          size: PAGE_SIZE,
          search: searchTerm || undefined,
          structureId: structureId || undefined,
          structureBeneficiaireId: beneficiaireId || undefined,
          annee: annee || undefined,
          sortBy,
          sortDir,
        });
        setResult(data);
      } catch (error) {
        console.error('Erreur lors du chargement des procédures:', error);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [structuresData, yearsData] = await Promise.all([
          apiService.getStructures(),
          apiService.getDistinctYears(),
        ]);
        setStructures(structuresData);
        setYears(yearsData);
      } catch (error) {
        console.error('Erreur chargement métadonnées:', error);
      }
    };
    loadMeta();
  }, []);

  useEffect(() => {
    fetchProcedures(currentPage, search, structureFixe || selectedStructure, selectedBeneficiaire, selectedYear, selectedSort);
  }, [currentPage, search, selectedStructure, selectedBeneficiaire, selectedYear, selectedSort, fetchProcedures, structureFixe]);

  useEffect(() => {
    if (viewMode !== 'gantt') return;
    const loadGantt = async () => {
      setGanttLoading(true);
      try {
        const [sortBy, sortDir] = selectedSort.split('_');
        const data = await apiService.getProcedures({
          size: 500,
          structureId: structureFixe || selectedStructure || undefined,
          structureBeneficiaireId: selectedBeneficiaire || undefined,
          annee: selectedYear || undefined,
          search: search || undefined,
          sortBy,
          sortDir,
        });
        setGanttProcedures(data.content);
      } catch (e) {
        console.error('Erreur chargement Gantt:', e);
      } finally {
        setGanttLoading(false);
      }
    };
    loadGantt();
  }, [viewMode, selectedStructure, selectedBeneficiaire, selectedYear, search, selectedSort, structureFixe]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setCurrentPage(0);
    }, 400);
  };

  const handleStructureChange = (value: string) => {
    setSelectedStructure(value);
    setCurrentPage(0);
  };

  const handleYearChange = (value: string) => {
    setSelectedYear(value ? Number(value) : '');
    setCurrentPage(0);
  };

  const handleBeneficiaireChange = (value: string) => {
    setSelectedBeneficiaire(value);
    setCurrentPage(0);
  };

  const handleSortChange = (value: string) => {
    setSelectedSort(value);
    setCurrentPage(0);
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setSelectedStructure('');
    setSelectedBeneficiaire('');
    setSelectedYear('');
    setSelectedSort('createdAt_desc');
    setCurrentPage(0);
  };

  const hasActiveFilters = !!(searchInput || selectedStructure || selectedBeneficiaire || selectedYear || selectedSort !== 'createdAt_desc');

  const handleDelete = async () => {
    if (!procedureToDelete) return;
    setIsDeleting(true);
    try {
      await apiService.deleteProcedure(procedureToDelete.id);
      fetchProcedures(currentPage, search, structureFixe || selectedStructure, selectedBeneficiaire, selectedYear, selectedSort);
      setShowDeleteModal(false);
      setProcedureToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const canModify = (procedure: Procedure) => {
    if (isSuperAdmin()) return true;
    if (isResponsable() && user?.structureId === procedure.structureId) return true;
    return false;
  };

  const isOwnStructure = (procedure: Procedure) =>
    isUtilisateur && user?.structureId === procedure.structureId;

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Procédures</h1>
          <p className="mt-1 text-sm text-gray-500 flex items-center gap-1.5">
            {isUtilisateur && user?.structureNom && (
              <>
                <BuildingOffice2Icon className="h-4 w-4 text-primary-500" />
                <span className="font-medium text-primary-600">{user.structureNom}</span>
                <span className="text-gray-300">—</span>
              </>
            )}
            <span>
              {result.totalElements} procédure{result.totalElements !== 1 ? 's' : ''} trouvée
              {result.totalElements !== 1 ? 's' : ''}
            </span>
          </p>
        </div>

        {(isSuperAdmin() || isResponsable()) && (
          <Link
            to="/procedures/new"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 shadow-sm hover:shadow-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="h-5 w-5" />
            Nouvelle procédure
          </Link>
        )}
      </div>

      {/* ── Sélecteur de vue ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
          <button
            onClick={() => setViewMode('cards')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150 ${
              viewMode === 'cards'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Squares2X2Icon className="h-4 w-4" />
            <span className="hidden sm:inline">Cartes</span>
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150 ${
              viewMode === 'table'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <TableCellsIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Tableau</span>
          </button>
          {isSuperAdmin() && (
            <button
              onClick={() => setViewMode('gantt')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                viewMode === 'gantt'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <CalendarDaysIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Gantt</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Barre de filtres ── */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          {/* Icône filtre */}
          <div className="hidden sm:flex items-center justify-center w-9 h-9 rounded-xl bg-gray-50 border border-gray-200 flex-shrink-0">
            <FunnelIcon className="h-4 w-4 text-gray-400" />
          </div>

          {/* Recherche */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Rechercher une procédure..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="block w-full pl-9 pr-9 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm placeholder-gray-400 transition-colors"
            />
            {searchInput && (
              <button
                onClick={() => handleSearchChange('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filtre structure de développement (SuperAdmin uniquement) */}
          {isSuperAdmin() && (
            <div className="sm:w-48">
              <select
                value={selectedStructure}
                onChange={(e) => handleStructureChange(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-700 bg-white transition-colors"
                title="Filtrer par structure de développement"
              >
                <option value="">Struct. dev.</option>
                {structures.map((s) => (
                  <option key={s.id} value={s.id}>{s.sigle || s.nom}</option>
                ))}
              </select>
            </div>
          )}

          {/* Filtre structure bénéficiaire */}
          {isSuperAdmin() && (
            <div className="sm:w-48">
              <select
                value={selectedBeneficiaire}
                onChange={(e) => handleBeneficiaireChange(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm text-gray-700 bg-white transition-colors"
                title="Filtrer par structure bénéficiaire"
              >
                <option value="">Struct. bénéf.</option>
                {structures.map((s) => (
                  <option key={s.id} value={s.id}>{s.sigle || s.nom}</option>
                ))}
              </select>
            </div>
          )}

          {/* Filtre année */}
          <div className="sm:w-32">
            <select
              value={selectedYear}
              onChange={(e) => handleYearChange(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-700 bg-white transition-colors"
            >
              <option value="">Année</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          {/* Tri */}
          <div className="sm:w-52 relative">
            <BarsArrowDownIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <select
              value={selectedSort}
              onChange={(e) => handleSortChange(e.target.value)}
              className="block w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-700 bg-white transition-colors"
            >
              <option value="createdAt_desc">Plus récentes</option>
              <option value="createdAt_asc">Plus anciennes</option>
              <option value="ponderationScore_desc">Meilleur score</option>
              <option value="ponderationScore_asc">Moins bon score</option>
              <option value="tachesTotal_desc">Plus de tâches</option>
              <option value="tachesTotal_asc">Moins de tâches</option>
              <option value="tachesCompleted_desc">Plus avancées</option>
              <option value="tachesCompleted_asc">Moins avancées</option>
              <option value="nom_asc">Nom A → Z</option>
              <option value="nom_desc">Nom Z → A</option>
              <option value="dateFin_asc">Échéance proche</option>
              <option value="dateFin_desc">Échéance lointaine</option>
            </select>
          </div>

          {/* Reset filtres */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 hover:text-gray-800 transition-colors whitespace-nowrap"
            >
              <XMarkIcon className="h-4 w-4" />
              Réinitialiser
            </button>
          )}
        </div>

        {/* Filtre structure fixe (utilisateur) — informatif */}
        {isUtilisateur && user?.structureNom && (
          <div className="mt-3 flex items-center gap-2 text-xs text-primary-700 bg-primary-50 px-3 py-2 rounded-lg border border-primary-100">
            <CheckCircleIconSolid className="h-4 w-4 text-primary-500 flex-shrink-0" />
            <span>
              Filtre automatique appliqué :{' '}
              <strong>{user.structureNom}</strong>
            </span>
          </div>
        )}
      </div>

      {/* ── Vue Gantt (super admin) ── */}
      {viewMode === 'gantt' && (
        ganttLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3 text-gray-400">
              <svg className="animate-spin h-8 w-8 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              <span className="text-sm">Chargement du diagramme…</span>
            </div>
          </div>
        ) : (
          <ProcedureGanttChart procedures={ganttProcedures} />
        )
      )}

      {/* ── Vue Tableau ── */}
      {viewMode === 'table' && (
        isLoading ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden animate-pulse">
            <div className="h-12 bg-gray-100 border-b" />
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex gap-4 px-5 py-3 border-b items-center">
                <div className="h-4 bg-gray-200 rounded w-1/4" />
                <div className="h-4 bg-gray-100 rounded w-1/5" />
                <div className="h-4 bg-gray-100 rounded w-1/6" />
                <div className="h-4 bg-gray-100 rounded w-12" />
                <div className="h-2 bg-gray-200 rounded-full w-20" />
                <div className="h-5 bg-gray-200 rounded-full w-20 ml-auto" />
              </div>
            ))}
          </div>
        ) : result.content.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 flex flex-col items-center text-center">
            <DocumentTextIcon className="h-12 w-12 text-gray-200 mb-3" />
            <h3 className="text-base font-semibold text-gray-700 mb-1">
              {hasActiveFilters ? 'Aucun résultat' : 'Aucune procédure'}
            </h3>
            <p className="text-sm text-gray-400 max-w-xs">
              {hasActiveFilters ? 'Modifiez ou réinitialisez vos filtres.' : 'Créez une première procédure.'}
            </p>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 text-gray-600 transition-colors">
                <XMarkIcon className="h-4 w-4" /> Réinitialiser
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Procédure</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap hidden md:table-cell">Struct. dév.</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap hidden lg:table-cell">Struct. bénéf.</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Année</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Pondération</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap hidden sm:table-cell">Statut</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap hidden xl:table-cell">Échéance</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {result.content.map((procedure, idx) => {
                    const statut = getDisplayStatut(procedure);
                    const cfg = STATUT_CONFIG[statut];
                    const StatutIcon = cfg.icon;
                    const scoreColor = procedure.ponderationScore >= 70
                      ? 'bg-green-500'
                      : procedure.ponderationScore >= 40
                      ? 'bg-amber-500'
                      : 'bg-red-500';
                    return (
                      <tr key={procedure.id} className={`hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                        {/* Nom */}
                        <td className="px-4 py-3">
                          <Link
                            to={`/procedures/${procedure.id}`}
                            className="font-medium text-gray-900 hover:text-primary-700 transition-colors line-clamp-1"
                          >
                            {procedure.nom}
                          </Link>
                          <p className="text-xs text-gray-400 mt-0.5 md:hidden">{procedure.structureNom ?? '—'}</p>
                        </td>
                        {/* Structure dév. */}
                        <td className="px-4 py-3 text-gray-600 text-xs hidden md:table-cell">
                          <span className="truncate max-w-[160px] block">{procedure.structureNom ?? '—'}</span>
                        </td>
                        {/* Structure bénéf. */}
                        <td className="px-4 py-3 text-xs hidden lg:table-cell">
                          {procedure.structureBeneficiaireNom
                            ? <span className="text-indigo-600 truncate max-w-[140px] block">{procedure.structureBeneficiaireNom}</span>
                            : <span className="text-gray-300 italic">—</span>}
                        </td>
                        {/* Année */}
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{procedure.annee}</span>
                        </td>
                        {/* Pondération */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-gray-100 rounded-full h-1.5 flex-shrink-0 overflow-hidden">
                              <div
                                className={`h-1.5 rounded-full ${scoreColor} transition-all`}
                                style={{ width: `${procedure.ponderationScore}%` }}
                              />
                            </div>
                            <span className="text-xs font-bold text-gray-700 whitespace-nowrap">
                              {procedure.ponderationScore}%
                            </span>
                          </div>
                        </td>
                        {/* Statut */}
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 whitespace-nowrap ${cfg.badgeCls}`}>
                            <StatutIcon className="h-3 w-3" />
                            {cfg.label}
                          </span>
                        </td>
                        {/* Échéance */}
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap hidden xl:table-cell">
                          {new Date(procedure.dateFin).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        {/* Actions */}
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {canModify(procedure) && (
                              <>
                                <button
                                  onClick={() => navigate(`/procedures/${procedure.id}/edit`)}
                                  title="Modifier"
                                  className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => { setProcedureToDelete(procedure); setShowDeleteModal(true); }}
                                  title="Supprimer"
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {/* ── Vue Cartes ── */}
      {viewMode === 'cards' && (
        isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : result.content.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 flex flex-col items-center text-center">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4 ring-4 ring-gray-100">
              <DocumentTextIcon className="h-9 w-9 text-gray-300" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              {hasActiveFilters ? 'Aucun résultat' : 'Aucune procédure'}
            </h3>
            <p className="text-sm text-gray-500 max-w-xs mb-6">
              {hasActiveFilters
                ? 'Aucune procédure ne correspond à vos critères. Essayez de modifier ou réinitialiser vos filtres.'
                : 'Commencez par créer une première procédure pour votre structure.'}
            </p>
            {hasActiveFilters ? (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
                Réinitialiser les filtres
              </button>
            ) : (
              (isSuperAdmin() || isResponsable()) && (
                <Link
                  to="/procedures/new"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 shadow-sm transition-all"
                >
                  <PlusIcon className="h-5 w-5" />
                  Nouvelle procédure
                </Link>
              )
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {result.content.map((procedure) => (
              <ProcedureCard
                key={procedure.id}
                procedure={procedure}
                canEdit={canModify(procedure)}
                isOwnStructure={isOwnStructure(procedure)}
                onEdit={() => navigate(`/procedures/${procedure.id}/edit`)}
                onDelete={() => {
                  setProcedureToDelete(procedure);
                  setShowDeleteModal(true);
                }}
              />
            ))}
          </div>
        )
      )}

      {/* ── Pagination (cartes + tableau) ── */}
      {!isLoading && viewMode !== 'gantt' && result.content.length > 0 && (
        <PaginationBar
          page={result.page}
          totalPages={result.totalPages}
          totalElements={result.totalElements}
          size={result.size}
          onPageChange={setCurrentPage}
        />
      )}

      {/* ── Modal suppression ── */}
      {showDeleteModal && procedureToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
            onClick={() => !isDeleting && setShowDeleteModal(false)}
          />

          {/* Dialog */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* En-tête danger */}
            <div className="bg-red-50 border-b border-red-100 px-6 py-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Supprimer la procédure</h3>
                <p className="text-sm text-gray-500">Cette action est irréversible</p>
              </div>
            </div>

            {/* Contenu */}
            <div className="px-6 py-5">
              <p className="text-sm text-gray-600">
                Êtes-vous sûr de vouloir supprimer{' '}
                <span className="font-semibold text-gray-900">
                  "{procedureToDelete.nom}"
                </span>{' '}
                ? Toutes les données associées (tâches, pondérations) seront définitivement perdues.
              </p>
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-70"
              >
                {isDeleting && (
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                )}
                {isDeleting ? 'Suppression...' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Procedures;
